import React, { useState, useRef } from "react";
import { Modal, Form, Image } from "react-bootstrap";
import Loader from "../../../common/Loader";
import { showErrorToast, showSuccessToast } from "../../../../common/helpers/Utils";
import delivaApiService from "../../../../common/services/apiService";
import PaperClipIcon from "../../../../assets/images/icons/Paperclip.svg";
import { ReactComponent as CSVSample } from "../../../../assets/images/icons/CSVsample.svg";
import {ReactComponent as ExcelSample } from "../../../../assets/images/icons/ExcelSample.svg";
import "./AddEmployee.scss";

const AddEmployee = ({ orgId, employee, onHide, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [employeeEmailOrPhoneData, setEmployeeEmailOrPhoneData] = useState([]);
  const [addEmployeeType, setAddEmployeeType] = useState("manual");
  const [role, setRole] = useState("DELIVERY_PARTNER_ADMIN");
  const [errors, setErrors] = useState({});
  const [csvFile, setCsvFile] = useState(null);
  const isMountedRef = useRef(true);

  const isValidEmail = (value) => {
    return /^(?!.*(\.[a-zA-Z]{2,})(?:\1)+)[a-zA-Z0-9._%+-]+@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/.test(value) && /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,6}$/.test(
      value,
    );
  };

  const isValidPhone = (value) => {
    return /^[0-9+\-\s()]+$/.test(value) && value.replace(/\D/g, '').length >= 10 && value.replace(/\D/g, '').length <= 15;
  };

  const validateForm = (type) => {
    const newErrors = {};

    if (type) {
      if (!emailOrPhone.trim() && !employeeEmailOrPhoneData.length) {
        newErrors.emailOrPhone = "Email or contact is required";
      } else if (
        !isValidEmail(emailOrPhone) &&
        !isValidPhone(emailOrPhone) &&
        emailOrPhone.trim()
      ) {
        newErrors.emailOrPhone =
          "Please enter a valid email or contact";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddEmployee = async () => {
    if (!validateForm("add")) {
      return;
    }

    setLoading(true);
    try {
      const formData = {
        orgId: orgId,
        employeeRole: role,
        employeeEmails: emailOrPhone ? [emailOrPhone] : employeeEmailOrPhoneData,
      };

      const { data: response } = await delivaApiService(
        "addEmployeeIntoOrganization",
        formData,
      );

      if (isMountedRef.current) {
        if (response?.resourceData?.status === 200) {
          onSuccess();
          showSuccessToast(response?.resourceData?.responseMessage || "Employee added successfully");
        } else {          
          showErrorToast(
            response?.resourceData?.responseMessage ||
              "Failed to add employee. Please try again.",
          );
        }
      }
    } catch (err) {
      console.error("Error adding employee:", err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleAddEmployeeEmailOrPhone = (e) => {
    e.preventDefault();
    if (!emailOrPhone.trim()) {
      setErrors({
        ...errors,
        emailOrPhone: "Email or contact is required",
      });
      return;
    }

    if (!isValidEmail(emailOrPhone) && !isValidPhone(emailOrPhone)) {
      setErrors({
        ...errors,
        emailOrPhone: "Please enter a valid email or contact",
      });
      return;
    }

    setEmployeeEmailOrPhoneData((prevData) => [
      ...prevData,
      emailOrPhone.trim(),
    ]);
    setErrors({});
    setEmailOrPhone("");
  };

  const handleRemoveEmailOrPhone = (emailOrPhone) => {
    setEmployeeEmailOrPhoneData((prevData) =>
      prevData.filter((item) => item !== emailOrPhone),
    );
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];

      if (!validTypes.includes(file.type)) {
        showErrorToast("Please upload a valid CSV or Excel file");
        return;
      }

      setCsvFile(file);
      setErrors({});
    }
  };

  const handleUploadCsv = async () => {
    if (!csvFile) {
      setErrors({ csvFile: "Please select a file to upload" });
      return;
    }

    setLoading(true);
    try {
      const response = await delivaApiService("importOrganizationEmployees", {
        orgId: orgId,
        file: csvFile,
      });

      if (isMountedRef.current) {
        if (response?.status === 200) {
          setCsvFile(null);
          onSuccess();
        }
      }
    } catch (err) {
      console.error("Error uploading CSV:", err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  // For downloading sample CSV/Excel files
  const downloadImage = async (url, filename = "image.jpg") => {
  const response = await fetch(url);
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

  return (
    <Modal
      show={true}
      centered
      size="lg"
      onHide={() => null}
      className="add-organization-modal"
    >
      <Modal.Header className="border-bottom">
        <Modal.Title className="fw-normal">{"Add Employee"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Loader showLoader={loading} />
        <Form className="add-organization-form">
          {/* Organization Name and Email */}
          <div className="mb-4">
            <div className="payment-transfer-options">
              <div className="checkboxContainer">
                <input
                  id="manual_add"
                  type="radio"
                  name="addEmployeeType"
                  value="manual"
                  checked={addEmployeeType === "manual"}
                  onChange={(e) => setAddEmployeeType(e.target.value)}
                />
                <label htmlFor="manual_add">Manual</label>
              </div>
              <div className="checkboxContainer">
                <input
                  id="csv_add"
                  type="radio"
                  name="addEmployeeType"
                  value="csv_upload"
                  checked={addEmployeeType === "csv_upload"}
                  onChange={(e) => setAddEmployeeType(e.target.value)}
                />
                <label htmlFor="csv_add">CSV</label>
              </div>
            </div>
          </div>

          {/* Payment Transfer To */}
          {addEmployeeType === "manual" ? (
            <>
              <div className="mb-4">
                <Form.Label>
                  <h3 className="font-weight-normal mb-3 line-ht color-33 fs20">
                    Role
                  </h3>
                </Form.Label>
                <div className="payment-transfer-options">
                  <div className="checkboxContainer">
                    <input
                      id="delivery_partner_admin"
                      type="radio"
                      name="role"
                      value="DELIVERY_PARTNER_ADMIN"
                      checked={role === "DELIVERY_PARTNER_ADMIN"}
                      onChange={(e) => setRole(e.target.value)}
                    />
                    <label htmlFor="delivery_partner_admin">
                      {" "}
                      {"Delivery Partner Admin"}
                    </label>
                  </div>
                  <div className="checkboxContainer">
                    <input
                      id="delivery_partner"
                      type="radio"
                      name="role"
                      value="DELIVERY_PARTNER"
                      checked={role === "DELIVERY_PARTNER"}
                      onChange={(e) => setRole(e.target.value)}
                    />
                    <label htmlFor="delivery_partner">
                      {" "}
                      {"Delivery Partner"}
                    </label>
                  </div>
                  
                </div>
              </div>
              <div className="d-flex gap-3 mb-2">
                <div className=" flex-grow-1">
                  <Form.Group className="mb-0">
                    <Form.Label htmlFor="organizationName">
                      <h6 className="fw-bold mb-2">Email / Contact</h6>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter email or contact"
                      value={emailOrPhone}
                      maxLength={50}
                      name="emailOrPhone"
                      onChange={(e) => setEmailOrPhone(e.target.value.trim( ))}
                      // isInvalid={!!errors.emailOrPhone}
                      className="login-input "
                    />
                  </Form.Group>
                </div>
                <div className=" d-flex align-items-end">
                  <button
                    className="button btnDefault btnPrimary mt-4 text-nowrap px-4 py-3"
                    onClick={handleAddEmployeeEmailOrPhone}
                  >
                    Add More
                  </button>
                </div>
              </div>
              {errors?.emailOrPhone && (
                <span className="text-danger">{errors?.emailOrPhone}</span>
              )}
              <div className="mt-3 ml-2">
                {employeeEmailOrPhoneData.map((emailOrPhone) => (
                  <span
                    key={emailOrPhone}
                    style={{
                      background: "#FFF3CD",
                      color: "#856404",
                      borderRadius: "16px",
                      padding: "4px 12px",
                      marginRight: "8px",
                      marginBottom: "4px",
                      display: "inline-block",
                      fontSize: 15,
                    }}
                  >
                    {emailOrPhone}
                    <span
                      style={{
                        cursor: "pointer",
                        marginLeft: 8,
                        fontWeight: "bold",
                      }}
                      onClick={() => handleRemoveEmailOrPhone(emailOrPhone)}
                    >
                      ×
                    </span>
                  </span>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="d-flex justify-content-between">
              <div className="mb-4">
                <Form.Label>
                  <span className="font-weight-normal mb-3 line-ht color-33 fs14">
                    Upload CSV or Excel File for delivery partner
                  </span>
                </Form.Label>
                <Form.Group className="mb-3">
                  <label
                    htmlFor="csvFileInput"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      border: "2px dashed #C8E6C9",
                      borderRadius: "8px",
                      padding: "24px",
                      height: "46px",
                      width: "350px",
                      textAlign: "center",
                      backgroundColor: "#F1F8F4",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#E8F5E9";
                      e.currentTarget.style.borderColor = "#81C784";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#F1F8F4";
                      e.currentTarget.style.borderColor = "#C8E6C9";
                    }}
                  >
                    <Image src={PaperClipIcon} alt="paper-pin" width={16} height={16}  />
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#000000",
                        fontWeight: "400",
                      }}
                    >
                      Upload file
                    </div>
                  </label>
                  <input
                    id="csvFileInput"
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                  />
                  {errors.csvFile && (
                    <Form.Control.Feedback
                      type="invalid"
                      style={{ display: "block" }}
                    >
                      {errors.csvFile}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                {csvFile && (
                  <div className="mt-3 mb-3">
                    <span
                      style={{
                        background: "#E8F5E9",
                        color: "#2E7D32",
                        borderRadius: "4px",
                        padding: "8px 12px",
                        display: "inline-block",
                        fontSize: 14,
                      }}
                    >
                      📄 {csvFile.name}
                      <span
                        style={{
                          cursor: "pointer",
                          marginLeft: 8,
                          fontWeight: "bold",
                        }}
                        onClick={() => setCsvFile(null)}
                      >
                        ×
                      </span>
                    </span>
                  </div>
                )}
              </div>
              <div className="mb-4">
                <Form.Label>
                  <span className="font-weight-normal mb-3 line-ht color-33 fs14">
                    Download CSV or Excel File
                  </span>
                </Form.Label>
                <div className="d-flex  gap-3">
                <div className="mb-3">
                  <span
                    style={{
                      display: "flex",
                      border: "2px solid #66BC48",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      borderRadius: "10px",
                      width: "155px",
                      height: "46px",
                      padding: "24px",
                      textAlign: "center",
                      backgroundColor: "#F1F8F4",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onClick={() => downloadImage("https://dev-deliva.s3.us-east-2.amazonaws.com/excel/org_employee.csv", "sample.csv")}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#E8F5E9";
                      e.currentTarget.style.borderColor = "#81C784";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#F1F8F4";
                      e.currentTarget.style.borderColor = "#C8E6C9";
                    }}
                  >
                    
                    <CSVSample width={16} height={16} />
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#2E7D32",
                        fontWeight: "500",
                      }}
                      
                    >
                      CSV
                    </div>
                    
                  </span>
                </div>
                <div className="mb-3">
                  <span
                    
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center", 
                      gap: "8px",
                      border: "2px solid #66BC48",
                      borderRadius: "10px",
                      width: "155px",
                      height: "46px",
                      padding: "24px",
                      textAlign: "center",
                      backgroundColor: "#F1F8F4",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onClick={() => downloadImage("https://dev-deliva.s3.us-east-2.amazonaws.com/excel/org_employee.xlsx", "sample.xlsx")}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#E8F5E9";
                      e.currentTarget.style.borderColor = "#81C784";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#F1F8F4";
                      e.currentTarget.style.borderColor = "#C8E6C9";
                    }}
                  >
                    
                    <ExcelSample width={16} height={16} />  
                    
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#2E7D32",
                        fontWeight: "500",
                      }}
                    >
                      Excel
                    </div>
                    
                  </span>
                </div>
                </div>

              </div></div>
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer className="border-top d-flex justify-content-start">
        <button
          variant="outline-secondary"
          onClick={onHide}
          className="button btnDefault btnOutline text-nowrap px-4 py-2"
        >
          Cancel
        </button>

        <button
          className="btnDefault btnPrimary minw-100 ml-3 px-2"
          onClick={
            addEmployeeType === "manual" ? handleAddEmployee : handleUploadCsv
          }
        >
          {"Add"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddEmployee;
